<template>
  <section id="chat-panel">
    <section
        id="chat-messages"
        v-show="!showPanelEmoji && !showPanelCommands && !showPanelGiphy && !themeSelectorEnabled && !publicGroupsSelected"
        v-images-loaded="scrollDownMessages"
        :class="[{'dark': isDarkMode}]"
    >

      <loader-large v-if="fetchingMessages"></loader-large>

      <ul v-if="!fetchingMessages" id="chat-messages-list">
        <li v-for="message in chatMessages" class="message"
            :key="message.type === 'group' ? message.messages[0].id : message.id">
          <a :href="`/Profile/${message.posterStringUrl}`" target="_blank"
             v-if="message.type !== 'group' && message.posterId !== 0">
            <img class="avatar" :src="generateAvatarImage(message)"/>
          </a>

          <a :href="`/Profile/${message.messages[0].posterStringUrl}`" target="_blank"
             v-if="message.type === 'group'">
            <img class="avatar" :src="generateAvatarImage(message.messages[0])"/>
          </a>

          <div class="message-details">
            <template v-if="message.type !== 'group' && message.posterId !== 0">
                            <span class="message-header">
                                <span class="alias" v-if="message.posterId !== ownId"
                                      @click="tagUser(message.posterAlias)">{{ message.posterAlias }}</span>
                                <span class="alias" v-if="message.posterId === ownId">You</span>
                                <span class="rating" v-if="message.posterId !== ownId">
                                    <i @click.prevent="voteUserUp(message.posterId)"
                                       :class="['fa fa-arrow-up up', {highlighted: message.loggedVoteValue === 1}]"></i>
                                    <i @click.prevent="voteUserDown(message.posterId)"
                                       :class="['fa fa-arrow-down down', {highlighted: message.loggedVoteValue === -1}]"></i>

                                    <span class="value">{{ message.voteValue }}</span>
                                    <span v-if="!message.isIgnored" class="ignore"
                                          @click.prevent="ignoreUser(message.posterId)">Ignore</span>
                                    <span v-if="message.isIgnored" class="ignore"
                                          @click.prevent="ignoreUser(message.posterId)">Un-Ignore</span>
                                </span>
                                <span class="rating" v-if="message.posterId === ownId"><span class="value">{{ message.voteValue }}</span></span>
                            </span>

              <div class="bubble">
                                <span v-if="!message.isIgnored && message.voteValue > userVoteThreshold"
                                      class="individual-message">
                                    <markdown-content :content="message.message"
                                                      class="individual-message-content"></markdown-content>
                                    <span class="timestamp">{{ message.timestamp | formatDate }}</span>
                                </span>

                <span v-if="message.isIgnored || message.voteValue <= userVoteThreshold"
                      class="individual-message">
                                    <markdown-content content="*::::Blocked Messages::::*"
                                                      class="individual-message-content"></markdown-content>
                                </span>
              </div>
            </template>

            <template v-if="message.type === 'group'">
                            <span class="message-header">
                                <span class="alias" v-if="message.messages[0].posterId !== ownId"
                                      @click="tagUser(message.messages[0].posterAlias)">
                                    {{ message.messages[0].posterAlias }}
                                </span>
                                <span class="alias" v-if="message.messages[0].posterId === ownId">You</span>
                                <span class="rating" v-if="message.messages[0].posterId !== ownId">
                                    <i @click.prevent="voteUserUp(message.messages[0].posterId)"
                                       :class="['fa fa-arrow-up up', {highlighted: message.messages[0].loggedVoteValue === 1}]"></i>
                                    <i @click.prevent="voteUserDown(message.messages[0].posterId)"
                                       :class="['fa fa-arrow-down down', {highlighted: message.messages[0].loggedVoteValue === -1}]"></i>
                                    <span class="value">{{ message.messages[0].voteValue }}</span>
                                    <span v-if="!message.messages[0].isIgnored" class="ignore"
                                          @click.prevent="ignoreUser(message.messages[0].posterId)">Ignore</span>
                                    <span v-if="message.messages[0].isIgnored" class="ignore"
                                          @click.prevent="ignoreUser(message.messages[0].posterId)">Un-Ignore</span>
                                </span>
                                <span class="rating" v-if="message.messages[0].posterId === ownId"><span class="value">{{ message.messages[0].voteValue }}</span></span>
                            </span>

              <div class="bubble">
                                <span v-if="!message.messages[0].isIgnored && message.messages[0].voteValue > userVoteThreshold"
                                      class="individual-message" v-for="singleMessage in message.messages">
                                    <markdown-content :content="singleMessage.message"
                                                      class="individual-message-content"></markdown-content>
                                    <span class="timestamp">{{ singleMessage.timestamp | formatDate }}</span>
                                </span>

                <span v-if="message.messages[0].isIgnored || message.messages[0].voteValue <= userVoteThreshold"
                      class="individual-message">
                                    <markdown-content content="*::::Blocked Messages::::*"
                                                      class="individual-message-content"></markdown-content>
                                </span>
              </div>
            </template>

            <template v-if="message.posterId === 0">
              <div class="bubble">
                                <span class="individual-message system-message">
                                    <markdown-content :content="message.message"
                                                      class="individual-message-content"></markdown-content>
                                    <span class="timestamp">{{ message.timestamp | formatDate }}</span>
                                </span>
              </div>
            </template>
          </div>
        </li>
      </ul>

    </section>

    <theme-selector v-if="themeSelectorEnabled" v-on:exitThemeSelectMode="exitThemeSelectMode"></theme-selector>

    <section id="chat-help-emoji" v-if="showPanelEmoji" :class="[{'dark': isDarkMode}]">
      <div class="emoji" v-for="emoji in emojiList" :title="emoji.alias" @click="addEmoji(emoji.emoji)">
        {{ emoji.emoji }}
      </div>
    </section>

    <section id="chat-help-commands" v-if="showPanelCommands" :class="[{'dark': isDarkMode}]">
      <h1>Chat Commands</h1>

      <div class="command-entry">
        <span class="command">some *text* example</span>
        <span class="description">Italicizes text: some <em>text</em> example</span>
      </div>

      <div class="command-entry">
        <span class="command">some **text** example</span>
        <span class="description">Bolds text: some <strong>text</strong> example</span>
      </div>

      <div class="command-entry">
        <span class="command">/me mesage</span>
        <span class="description">Sends an action message</span>
      </div>

      <div class="command-entry">
        <span class="command">/flip or /flipcoin</span>
        <span class="description">Flips a coin and gives the result</span>
      </div>

      <div class="command-entry">
        <span class="command">/roll</span>
        <span class="description">Rolls a Six-Sided Dice</span>
      </div>

      <div class="command-entry">
        <span class="command">/roll XdY</span>
        <span class="description">Rolls X Y-Sided Dice. Example: <em>/roll 4d10</em> rolls 4 10 sided dice.</span>
      </div>
    </section>

    <section id="chat-giphy-panel" v-if="showPanelGiphy" :class="[{'dark': isDarkMode}]">
      <div class="powered-by-giphy">
        <img src="/static/powered-by-giphy.png">
      </div>
      <div class="results-list">
        <loader-large v-if="giphySearching"></loader-large>

        <div class="result-container" v-if="!giphySearching" v-for="giphyResult in giphyResults"
             @click="addGiphyToChat(giphyResult.id)">
          <img :src="giphyResult.images.fixed_height.url">
        </div>
      </div>
      <input type="text" v-model="giphySearch" placeholder="Search for a Gif..." ref="giphyInput">
    </section>

    <section id="chat-public-groups-list" v-if="publicGroupsSelected" :class="[{'dark': isDarkMode}]">
      <h1>Public Groups</h1>
      <div class="validation" v-if="publicGroupList.length === 0">There are no public groups that you haven't
        joined
      </div>
      <ul v-if="publicGroupList.length > 0">
        <li v-for="publicGroup in publicGroupList">
          <button @click.prevent="joinPublicGroup(publicGroup.id)" :disabled="joiningPublicGroup">Join Group
          </button>
          <span>{{ publicGroup.memberCount }} Member<template v-if="publicGroup.memberCount !== 1">s</template></span>
          <span>{{ publicGroup.alias }}</span>
        </li>
      </ul>
    </section>

    <section id="chat-inputs" :class="[{'dark': isDarkMode}]">
      <form id="chat-input">
        <div class="chat-options">
          <i :class="['fa', 'fa-question-circle-o', 'option-help', {selected: showPanelCommands}]"
             @click.prevent="toggleCommandsPanel"></i>
          <i :class="['fa', 'fa-smile-o', 'option-emoji', {selected: showPanelEmoji}]"
             @click.prevent="toggleEmojiPanel"></i>
          <i :class="['fa', 'option-gif', {selected: showPanelGiphy}]"
             @click.prevent="toggleGiphyPanel">gif</i>
          <input id="attach-file" name="attach-file" type="file" style="display: none;" @change="uploadFile">

          <label for="attach-file" v-if="!isUploading"><i class="fa fa-paperclip option-attach"></i></label>
          <i v-if="isUploading" class="fa fa-spin fa-spinner"></i>
          <!--<i :class="['fa', 'fa-gamepad', 'option-game', {selected: gamePanelEnabled}]" @click.prevent="toggleGamePanel"></i>-->
          <i :class="['fa', 'fa-dashboard', 'option-theme', {selected: themeSelectorEnabled}]"
             @click.prevent="toggleThemeSelector"></i>
          <i :class="['fa', 'fa-users', 'option-public-groups', {selected: publicGroupsSelected}]"
             @click.prevent="togglePublicGroups"></i>
        </div>
        <div class="chat-box">
          <input type="text" placeholder="Type Here..." v-model="newMessage" ref="inputChatBox"
                 maxlength="600"/>
          <button @click.prevent="submitMessage">Send</button>
        </div>
      </form>
    </section>
  </section>
</template>

<script>
import axios from 'axios';
import _ from 'lodash';
import imagesLoaded from 'vue-images-loaded';
import ThemeSelector from './ThemeSelector.vue';

export default {
  name: 'ChatPanel',
  components: {
    'theme-selector': ThemeSelector
  },

  data() {
    return {
      newMessage: '',
      fetchingMessages: false,
      showPanelEmoji: false,
      showPanelCommands: false,
      showPanelGiphy: false,
      giphySearch: '',
      giphyResults: [],
      emojiList: [],
      shouldScrollDown: true,
      gamePanelEnabled: false,
      giphySearching: false,
      isUploading: false,
      themeSelectorEnabled: false,
      publicGroupsSelected: false,
      publicGroupList: [],
      joiningPublicGroup: false
    }
  },

  directives: {
    imagesLoaded
  },

  computed: {
    isDarkMode() {
      return this.$store.state.loggedUserModel.info.darkmode;
    },

    chatId() {
      return this.$store.state.activeChatModel.id;
    },

    ownId() {
      return this.$store.state.loggedUserModel.isLoggedIn ? this.$store.state.loggedUserModel.info.id : 0;
    },

    ignoredUsers() {
      return this.$store.state.ignoredUsersModel.ignoredUsers;
    },

    userVoteThreshold() {
      return this.$store.state.loggedUserModel.info.ignoreThreshold;
    },

    chatMessages() {
      let messages,
          output = [],
          ignoredUsers = this.ignoredUsers,
          useSwearFilters = this.$store.state.loggedUserModel.info.useSwearFilter;

      if (this.fetchingMessages) {
        return [];
      }

      messages = this.$store.state.chatMessagesModel.chatMessages[this.chatId];

      if (!messages) {
        return [];
      }

      if (useSwearFilters) {
        for (let i = 0; i < messages.length; i++) {
          messages[i].message = messages[i].message.replace(/fuck/ig, '****');
          messages[i].message = messages[i].message.replace(/shit/ig, '****');
          messages[i].message = messages[i].message.replace(/bitch/ig, '*****');
          messages[i].message = messages[i].message.replace(/cunt/ig, '****');
          messages[i].message = messages[i].message.replace(/pussy/ig, '*****');
          messages[i].message = messages[i].message.replace(/dick/ig, '****');
          messages[i].message = messages[i].message.replace(/cock/ig, '****');
          messages[i].message = messages[i].message.replace(/asshole/ig, '*******');
          messages[i].message = messages[i].message.replace(/fag/ig, '***');
          messages[i].message = messages[i].message.replace(/faggot/ig, '******');
          messages[i].message = messages[i].message.replace(/nigger/ig, '******');
          messages[i].message = messages[i].message.replace(/douche/ig, '******');
        }
      }

      for (let i = 0; i < messages.length; i++) {
        let streakCount = 0,
            j = i,
            groupMessages = [];

        messages[i].isIgnored = ignoredUsers.indexOf(messages[i].posterId) >= 0;

        while (j < messages.length - 1 && messages[j].posterId === messages[j + 1].posterId && messages[j].posterId !== 0) {
          streakCount++;
          j++;
        }

        if (streakCount > 0) {
          streakCount++;
          groupMessages = {
            type: 'group',
            messages: []
          };

          for (let k = 0; k < streakCount; k++) {
            groupMessages.messages.push(messages[k + i]);
          }

          output.push(groupMessages);
          i += streakCount;
          i--;
        } else {
          output.push(messages[i]);
        }
      }

      return output;
    }
  },

  methods: {
    fetchMessages() {
      if (!this.$store.state.chatMessagesModel.chatMessages[this.chatId]) {
        this.fetchingMessages = true;
        setTimeout(() => {
          this.$store.dispatch('chatMessagesModel/fetchChatMessages', {
            chatId: this.chatId,
            callback: () => {
              this.fetchingMessages = false;
            }
          });
        }, 500)
      }
    },

    fetchPublicGroups() {
      axios.get('/api/publicGroups').then(
          (response) => {
            if (!response.data.error) {
              this.publicGroupList = response.data;
            }
          }
      );
    },

    joinPublicGroup(groupId) {
      this.joiningPublicGroup = true;

      axios.post('/api/publicGroups/joinGroup', {
        groupId: groupId
      }).then(
          (response) => {
            if (response.data.errors) {
              return;
            }
            this.joiningPublicGroup = false;
            this.fetchPublicGroups();
            this.$store.dispatch('groupChatsModel/fetchGroupChats');
          },
          (response) => {
            this.joiningPublicGroup = false;
          }
      );
    },

    updateReadStatus() {
      setTimeout(() => {
        this.$store.dispatch('chatMessagesModel/updateReadStatus', {
          chatId: this.chatId
        });
      }, 500);

      if (this.chatId.split('.')[0] === 'private') {
        this.$store.commit('friendChatsModel/clearReadMessages', {chatId: parseInt(this.chatId.split('.')[1], 10)});
      }

      if (this.chatId.split('.')[0] === 'group') {
        this.$store.commit('groupChatsModel/clearReadMessages', {chatId: parseInt(this.chatId.split('.')[1], 10)});
      }

      if (this.chatId.split('.')[0] === 'public') {
        this.$store.commit('publicChatsModel/clearReadMessages', {chatId: parseInt(this.chatId.split('.')[1], 10)});
      }
    },

    fetchEmoji() {
      axios.get('/api/emoji').then(
          (response) => {
            this.emojiList = response.data;
          }
      )
    },

    addEmoji(emoji) {
      if (this.newMessage.trim() !== '') {
        this.newMessage += ' ';
      }

      this.newMessage += emoji;

      this.$refs.inputChatBox.focus();
    },

    generateAvatarImage(message) {
      let hasAvatar = message.posterHasAvatar,
          id = message.posterId,
          avatarId = new Date(message.posterAvatarUpdate).getTime();

      if (message.isIgnored || message.voteValue <= this.userVoteThreshold) {
        hasAvatar = false;
      }

      if (hasAvatar) {
        return `/userdata/avatars/${id}.jpg?v=${avatarId}`
      } else {
        return '/defaultProfilePic.png';
      }
    },

    submitMessage() {
      this.showPanelEmoji = false;
      this.showPanelCommands = false;
      this.showPanelGiphy = false;
      this.themeSelectorEnabled = false;
      this.publicGroupsSelected = false;
      this.newMessage = this.newMessage.trim();

      this.$refs.inputChatBox.focus();

      if (this.newMessage === '') {
        return;
      }

      this.$store.dispatch('chatMessagesModel/sendMessage', {
        chatId: this.chatId,
        message: this.newMessage
      });

      this.newMessage = '';
    },

    scrollDownMessages() {
      let $chatMessages = document.getElementById('chat-messages-list');

      if ($chatMessages && this.shouldScrollDown) {
        $chatMessages.scrollTop = $chatMessages.scrollHeight;
      }
    },

    tagUser(userName) {
      if (this.newMessage.trim() !== '') {
        this.newMessage += ' ';
      }

      this.newMessage += '@' + userName + ': ';

      this.$refs.inputChatBox.focus();
    },

    toggleEmojiPanel() {
      this.showPanelEmoji = !this.showPanelEmoji;
      if (this.showPanelEmoji) {
        this.showPanelCommands = false;
        this.showPanelGiphy = false;
        this.themeSelectorEnabled = false;
        this.publicGroupsSelected = false;
      }
    },

    toggleCommandsPanel() {
      this.showPanelCommands = !this.showPanelCommands;
      if (this.showPanelCommands) {
        this.showPanelEmoji = false;
        this.showPanelGiphy = false;
        this.themeSelectorEnabled = false;
        this.publicGroupsSelected = false;
      }
    },

    toggleGiphyPanel() {
      this.showPanelGiphy = !this.showPanelGiphy;
      if (this.showPanelGiphy) {
        this.showPanelEmoji = false;
        this.showPanelCommands = false;
        this.themeSelectorEnabled = false;
        this.publicGroupsSelected = false;

        this.$nextTick(() => {
          this.$refs.giphyInput.focus();
        })

      }
    },

    toggleGamePanel() {
      this.gamePanelEnabled = !this.gamePanelEnabled;
    },

    toggleThemeSelector() {
      this.themeSelectorEnabled = !this.themeSelectorEnabled;
      if (this.themeSelectorEnabled) {
        this.showPanelEmoji = false;
        this.showPanelCommands = false;
        this.showPanelGiphy = false;
        this.publicGroupsSelected = false;
      }
    },

    togglePublicGroups() {
      this.publicGroupsSelected = !this.publicGroupsSelected;
      if (this.publicGroupsSelected) {
        this.showPanelEmoji = false;
        this.showPanelCommands = false;
        this.showPanelGiphy = false;
        this.themeSelectorEnabled = false;
      }
    },

    exitThemeSelectMode() {
      this.themeSelectorEnabled = false;
    },

    addGiphyToChat(giphyId) {
      this.showPanelEmoji = false;
      this.showPanelCommands = false;
      this.showPanelGiphy = false;

      this.$store.dispatch('chatMessagesModel/sendMessage', {
        chatId: this.chatId,
        message: `/giphy ${giphyId}`
      });

    },

    findGiphys: _.debounce(
        function () {
          this.giphySearch = this.giphySearch.trim();

          if (this.giphySearch === '') {
            return;
          }

          this.giphySearching = true;
          axios.get('https://api.giphy.com/v1/gifs/search', {
            params: {
              api_key: '09324ca51eb143778ee350fbc84ae948',
              q: this.giphySearch
            }
          }).then(
              (response) => {
                this.giphySearching = false;
                this.giphyResults = response.data.data;
              }
          )
        },
        500),

    ignoreUser(ignoredId) {
      this.$store.dispatch('ignoredUsersModel/toggleIgnoreStatus', {
        ignoredId,
        callback: () => {
          this.$store.dispatch('ignoredUsersModel/fetchIgnoredUsers', {chatId: this.chatId});
        }
      })
    },

    voteUserUp(userId) {
      this.sendUserVote(userId, 1);
    },

    voteUserDown(userId) {
      this.sendUserVote(userId, -1);
    },

    sendUserVote(userId, voteValue) {
      axios.post('/api/voteUser', {
        votedId: userId,
        voteValue: voteValue
      }).then(
          (response) => {
            this.$store.dispatch('chatMessagesModel/fetchChatMessages', {chatId: this.chatId})
          }
      )
    },

    uploadFile(e) {
      let input = e.target,
          file,
          formData = new FormData(),
          originatingChatId = this.chatId;

      if (input.files.length === 0) {
        return;
      }

      file = input.files[0];

      if (input.files[0].size > 10 * 1024 * 1024) {
        return;
      }

      this.isUploading = true;

      formData.append('uploadedFile', file);
      formData.append('chatId', this.chatId);

      axios.post('/api/fileUploader', formData).then(
          (response) => {
            this.isUploading = false;

            if (!response.data.errors && response.data.response === 'success') {
              let filename = response.data.filePath.split('/')[2];
              let message = 'Uploaded a File: [' + filename + '](' + (window.location.origin + escape(response.data.filePath)) + ')';

              this.$store.dispatch('chatMessagesModel/sendMessage', {
                chatId: originatingChatId,
                message: message
              });
            }
          }
      );
    },
  },

  mounted() {
    this.fetchMessages();
    this.fetchEmoji();
    this.fetchPublicGroups();

    this.$store.dispatch('videoChatModel/configure', () => {
      this.$store.dispatch('videoChatModel/connectToServer');
    });
  },

  sockets: {
    'updateChatMessages'(data) {
      let $chatMessages = document.getElementById('chat-messages-list');

      this.shouldScrollDown = $chatMessages && Math.abs(($chatMessages.scrollTop + $chatMessages.offsetHeight) - $chatMessages.scrollHeight) < 100;

      this.updateReadStatus();

      data.callback = () => {
        if (!this.$store.state.titleBarModel.isVisible && data.chatId.split('.')[0] !== 'public') {
          this.$store.state.titleBarModel.alert = this.$store.state.titleBarModel.defaultAlert
        }
      };

      this.$store.dispatch('chatMessagesModel/fetchChatMessages', data)
    }
  },

  watch: {
    'chatId'() {
      this.fetchMessages();
      this.updateReadStatus();
      this.$refs.inputChatBox.focus();
    },

    'chatMessages'() {
      this.$nextTick(() => {
        this.scrollDownMessages();
      });
    },

    'giphySearch'() {
      this.findGiphys();
    }
  }
}
</script>


// WEBPACK FOOTER //
// ChatPanel.vue?04c8a257